import React from 'react'
import { AgoraRtcCompatibilitySurface } from '../../providers/rtc/agora'
import SharedVideoCallShell from '../../ui/SharedVideoCallShell'
import { useRtcSessionHandleConfig } from '../useRtcSessionHandleConfig'

function invokeMaybe(handler, ...args) {
  if (typeof handler === 'function') {
    handler(...args)
  }
}

export default function SharedAgoraVideoCallSurface({
  shell,
  rtcProps,
  rtmProps,
  callbacks = {},
  styleProps = {},
  fallback = null,
  handleLeaveRoom,
  peerParticipantHint = null,
  layoutId = null,
  onLayoutChange = null,
  railStateId = null,
  onRailStateChange = null,
  showLayoutSelector = null,
  showRailSelector = null,
}) {
  const handleParticipantSnapshot = shell?.handleParticipantSnapshot
  const handleConnectionStateChange = shell?.handleConnectionStateChange
  const registerRuntimeCommands = shell?.registerRuntimeCommands
  const handleScreenShareStateChange = shell?.handleScreenShareStateChange
  const sessionHandle = shell?.sessionHandle

  const mergedCallbacks = React.useMemo(
    () => ({
      ...callbacks,
      'participants-updated': (snapshot) => {
        handleParticipantSnapshot?.(snapshot)
        invokeMaybe(callbacks?.['participants-updated'], snapshot)
      },
      'connection-state-change': (sessionState, previousState, reason) => {
        handleConnectionStateChange?.(sessionState, previousState, reason)
        invokeMaybe(
          callbacks?.['connection-state-change'],
          sessionState,
          previousState,
          reason,
        )
      },
      'register-runtime-commands': (commands) => {
        registerRuntimeCommands?.(commands, sessionHandle)
        invokeMaybe(callbacks?.['register-runtime-commands'], commands)
      },
      'screen-share-state-changed': (active) => {
        handleScreenShareStateChange?.(active)
        invokeMaybe(callbacks?.['screen-share-state-changed'], active)
      },
    }),
    [
      callbacks,
      handleConnectionStateChange,
      handleParticipantSnapshot,
      handleScreenShareStateChange,
      registerRuntimeCommands,
      sessionHandle,
    ],
  )
  const compatibilityConfig = useRtcSessionHandleConfig(shell?.sessionHandle)
  const resolvedRtcProps = compatibilityConfig?.rtcProps || rtcProps
  const resolvedRtmProps = compatibilityConfig?.rtmProps || rtmProps
  const agoraSurfaceKey = React.useMemo(
    () =>
      [
        resolvedRtcProps?.appId || '',
        resolvedRtcProps?.channel || '',
        resolvedRtcProps?.uid ?? '',
        resolvedRtmProps?.uid || '',
      ].join(':'),
    [
      resolvedRtcProps?.appId,
      resolvedRtcProps?.channel,
      resolvedRtcProps?.uid,
      resolvedRtmProps?.uid,
    ],
  )

  return (
    <AgoraRtcCompatibilitySurface
      key={agoraSurfaceKey}
      fallback={fallback}
      renderless
      rtcProps={resolvedRtcProps}
      rtmProps={resolvedRtmProps}
      styleProps={styleProps}
      callbacks={mergedCallbacks}
    >
      <SharedVideoCallShell
        shell={shell}
        sessionInterfaceId={shell?.sessionInterfaceId}
        peerParticipantHint={peerParticipantHint}
        handleLeaveRoom={handleLeaveRoom}
        providerFallback={fallback}
        layoutId={layoutId}
        railStateId={railStateId}
        onLayoutChange={onLayoutChange}
        onRailStateChange={onRailStateChange}
        showLayoutSelector={showLayoutSelector}
        showRailSelector={showRailSelector}
      />
    </AgoraRtcCompatibilitySurface>
  )
}

SharedAgoraVideoCallSurface.displayName = 'SharedAgoraVideoCallSurface'
